const installed = new WeakSet();
const ACTIVE_FILE_STATUSES = [2, 10]; // EVAPORATING, ERROR in the pinned EvaporateJS 2.1.4.

// Compatibility fix for the exact Evaporate commit pinned in package.json.
// Keep regression coverage against its real scheduler when upgrading it.
export default function installEvaporateSchedulerFix(evaporate) {
    if(installed.has(evaporate)) return evaporate;
    const pause = evaporate._pause;
    const queueFile = evaporate.queueFile;
    const installedFiles = new WeakSet();
    if(typeof pause !== 'function' || typeof queueFile !== 'function') {
        throw new Error('Unsupported Evaporate scheduler');
    }

    evaporate._pause = function(file, force, promises) {
        // startNextFile reserves one slot before multipart initiation. A force
        // pause normally releases slots in abortParts, but there are no parts
        // to abort during initiation or while awaiting completion confirmation.
        // Queued/paused files have no reservation and must not decrement it.
        const releaseEmptyReservation = force && ACTIVE_FILE_STATUSES.includes(file.status) &&
            this.filesInProcess.includes(file) && file.partsInProcess.length === 0;
        pause.call(this, file, force, promises);
        if(force) {
            // An aborted signer never reaches sendRequestToAWS, so its started
            // promise never resolves. Do not make resumed parts wait on it.
            file.lastPartSatisfied = Promise.resolve();
            // Paused requests bypass the library's error handler, which normally
            // clears the v4 canonical-request cache. Resume changes x-amz-date;
            // retaining the old canonical request produces SignatureDoesNotMatch.
            file.s3Parts.filter(part => part && part.status !== 3)
                .forEach(part => part.awsRequest.signer.error());
        }
        if(releaseEmptyReservation) {
            this.evaporatingCnt(-1);
            this.startNextFile('force pause released file slot');
        }
    };

    evaporate.queueFile = function(file) {
        if(!installedFiles.has(file)) {
            const uploadFileFromScratch = file.uploadFileFromScratch;
            let startingUpload;
            file.uploadFileFromScratch = function(...args) {
                // Resume before S3 returns an upload ID must join the existing
                // initiation, not create a second multipart upload for this file.
                if(startingUpload) return startingUpload;
                const attempt = uploadFileFromScratch.apply(this, args);
                startingUpload = attempt?.finally(() => { startingUpload = undefined; });
                return startingUpload;
            };
            const uploadParts = file.uploadParts;
            file.uploadParts = function() {
                if([0, 4, 30].includes(this.status)) { // PENDING (queued), PAUSED, PAUSING
                    // Initiation can finish after pause. Prepare the parts but
                    // leave them paused/queued; normal start/consumeSlots sends
                    // them after resume has a slot. Rejecting here aborts the
                    // upload; starting here could bypass the concurrency limit.
                    this.loaded = 0;
                    this.totalUploaded = 0;
                    return Promise.all(this.makeParts());
                }
                return uploadParts.call(this);
            };
            const makeParts = file.makeParts;
            file.makeParts = function(...args) {
                const promises = makeParts.apply(this, args);
                this.s3Parts.filter(Boolean).forEach(part => {
                    const request = part.awsRequest;
                    if(part.status === 3) return; // COMPLETE parts retain their request.
                    const send = request.send;
                    request.send = function() {
                        // Resetting the start gate can release both an old and a
                        // resumed continuation for a later part. Only one may
                        // sign/send it; ERROR and PENDING parts remain retryable.
                        if(this.part.status === 2) return;
                        return send.call(this);
                    };
                });
                return promises;
            };
            const changeCount = file.evaporatingCnt;
            file.evaporatingCnt = function(increment) {
                // Part completion removes the part before adjusting the count.
                // Keep this active file's base reservation when its sole part
                // finishes and more remain: getRemainingSlots will reuse it.
                // A force pause sets PAUSING first, so abortParts still releases
                // every active part normally.
                if(increment === -1 && ACTIVE_FILE_STATUSES.includes(this.status) &&
                    this.partsInProcess.length === 0 && this.partsToUpload.length > 0) return;
                changeCount.call(this, increment);
            };
            installedFiles.add(file);
        }
        queueFile.call(this, file);
        // An earlier file waiting on initiation must not prevent another batch
        // from using available capacity. startNextFile enforces the part limit.
        this.startNextFile('file queued');
    };

    evaporate.consumeRemainingSlots = function() {
        // Even at capacity, a part-less active file can reuse its reservation.
        // Each file enforces the shared limit through getRemainingSlots.
        this.filesInProcess.forEach(file => file.consumeSlots());
    };
    installed.add(evaporate);
    return evaporate;
}
